import * as fs from "node:fs";
import * as path from "node:path";
import type { ExtensionConfig, ToolDescriptionMode } from "../shared/types.ts";
import { getAgentDir, getProjectConfigDir } from "../shared/utils.ts";

const CUSTOM_TOOL_DESCRIPTION_FILE = "subagent-tool-description.md";
const CUSTOM_TOOL_DESCRIPTION_MAX_BYTES = 50 * 1024;

export const SUBAGENT_SAFETY_GUIDANCE = `SAFETY-CRITICAL SUBAGENT GUIDANCE:
• Use { action: "list" } before execution and only run executable/non-disabled agents.
• Direct execution is one child call: omit action, pass { agent?: "worker", task: "..." }. Omitted agent means worker.
• Direct calls wait for the child by default. Set async:true only when deliberately running background work; use subagent_wait for a result needed in this turn.
• The parent owns sequencing, lifecycle, and any parallel fanout. Children are workers, not orchestrators; they may contact the parent with contact_supervisor when blocked or needing a decision.
• Keep one writer for the same cwd/worktree. Use fresh-context read-only reviewers for independent review, then have the parent synthesize and apply fixes.
• Async runs expose asyncId/asyncDir with status.json, events.jsonl, output logs, and status via { action: "status", id }. Include output paths and residual risks when reporting results.`;

export const FULL_SUBAGENT_TOOL_DESCRIPTION = `Run one subagent directly with { agent?, task }; omit action. Omitted agent defaults to the built-in worker. Use action only for management/control. Use workflowScript only when the parent explicitly needs one durable scripted orchestration request.

DIRECT EXECUTION:
• Before executing, use { action: "list" } and run only executable/non-disabled agents.
• { agent: "reviewer", task: "Review the current diff for correctness." } runs one child and waits for the result.
• Omit agent to use worker. Pass model and thinking to override this child. Pass context:"fresh" (default for worker) or context:"fork" when the child must branch the parent transcript.
• The parent, not the child, owns the overall plan. For deliberate background or parallel work, make explicit async:true calls, then manage them with { action:"status" }, { action:"steer" }, { action:"stop" }, or subagent_wait.
• For repository mutation lanes, use worktree:true with runs.run/runs.all for managed isolation.

WORKFLOW SCRIPT (ADVANCED):
• { workflowScript: "return runs.run('main', {agent:'worker', task:'...'})" } is the explicit scripted orchestration surface. Every workflow is a workflowScript; use stable-key runs.run for one child and runs.all for parallel children. workflowScript is an ordinary JavaScript statement body, so use an explicit return for a useful result. Scripts start asynchronously by default; pass async:false only for a small foreground workflow. Same-repo foreground workflows default to a live in-chat card; set chatProgress to auto, off, or live-card to control that projection. Workflow-level child controls default onto each runs.run launch, and explicit child fields override them.
• Sequential example: { workflowScript: "const a = await runs.run('analyze', {agent:'researcher', task:'Analyze the request'}); return (await runs.run('plan', {agent:'worker', task:'Plan from: '+a.output})).output" }
• Parallel example: { workflowScript: "const [a,b] = await runs.all([{key:'correctness',agent:'reviewer',task:'Review correctness'},{key:'tests',agent:'reviewer',task:'Review tests'}]); return {correctness:a.output,tests:b.output}" }
• Durable mission attachment is automatic by default. Use missionId to attach an existing mission, mission:{...} to override auto-create, or mission:false for ephemeral work.

MANAGEMENT / CONTROL:
• list, get, models, children.list, create, update, delete, eject, disable, enable, reset, doctor, grant-spawn-budget, worktree.discard, refine/refine.show/refine.rollback, mission.create/list/show/update/attach-run/close, inspector.open/status/close, project.open/status/close, and watchdog actions remain available.
• status, interrupt, stop, resume, and steer manage live or persisted runs. Use status view:"fleet" for an overview or view:"transcript" with id and optional index to tail output.
• { action: "append-step", id: "...", step: {agent:"agent-c", task:"Use {previous}"} } appends one step to an already-running durable legacy chain. step is control-only, not a public execution mode.
• Create durable project schedules with { action:"schedule.create", id?, name?, at:"+10m" | ISO, workflowScript:"return runs.run('main', {agent:'worker', task:'...'})" } or { every:"6h", workflowScript:"..." }. Manage them with schedule.list/show/history/pause/resume/run/run-due/delete.

${SUBAGENT_SAFETY_GUIDANCE}`;

export const COMPACT_SUBAGENT_TOOL_DESCRIPTION = `Run one subagent directly with { agent?, task }; omit action. Omitted agent defaults to worker. Use action only for management/control.

EXECUTE:
• Call { action:"list" } first and use only executable/non-disabled agents.
• DIRECT {agent:"reviewer",task:"Review the diff"} waits synchronously by default. Use {agent:"worker",task:"...",async:true} for deliberate background work; use subagent_wait when this turn needs the result.
• The parent owns sequencing, lifecycle, and parallel fanout. Children are workers, not orchestrators; use contact_supervisor only for decisions, structured input, or meaningful progress updates.
• model and thinking override the direct child. context can be fresh or fork. For repository mutation lanes, use worktree:true with runs.run/runs.all for managed isolation.

WORKFLOW / MANAGE:
• Use workflowScript for explicit scripted orchestration: runs.run for one child and runs.all for parallel work, plus retries and aggregation. Scripts are ordinary JavaScript statement bodies; use explicit return.
• Use action without execution fields for list/get/models/authoring, mission, watchdog, status, interrupt, stop, resume, steer, scheduling, diagnostics, and other management actions.
• Async workflow and child status live under asyncId/asyncDir with status.json, events.jsonl, output logs, and {action:"status",id:"..."}.

${SUBAGENT_SAFETY_GUIDANCE}`;

function isToolDescriptionMode(value: unknown): value is ToolDescriptionMode {
	return value === "full" || value === "compact" || value === "custom";
}

function warn(options: ToolDescriptionOptions | undefined, message: string): void {
	(options?.warn ?? console.warn)(`[pi-subagents] ${message}`);
}

export interface ToolDescriptionOptions {
	cwd?: string;
	agentDir?: string;
	warn?: (message: string) => void;
}

export function resolveToolDescriptionMode(
	config: Pick<ExtensionConfig, "toolDescriptionMode">,
	options?: ToolDescriptionOptions,
): ToolDescriptionMode {
	const mode = config.toolDescriptionMode;
	if (mode === undefined) return "full";
	if (isToolDescriptionMode(mode)) return mode;
	warn(
		options,
		`Ignoring invalid toolDescriptionMode ${JSON.stringify(mode)}; expected "full", "compact", or "custom".`,
	);
	return "full";
}

function customDescriptionPaths(options?: ToolDescriptionOptions): string[] {
	const cwd = options?.cwd ?? process.cwd();
	const agentDir = options?.agentDir ?? getAgentDir();
	return [
		path.join(getProjectConfigDir(cwd), CUSTOM_TOOL_DESCRIPTION_FILE),
		path.join(agentDir, CUSTOM_TOOL_DESCRIPTION_FILE),
	];
}

function renderCustomTemplate(template: string, options?: ToolDescriptionOptions): string {
	const cwd = options?.cwd ?? process.cwd();
	const agentDir = options?.agentDir ?? getAgentDir();
	const projectConfigDir = getProjectConfigDir(cwd);
	const variables: Record<string, () => string> = {
		fullDescription: () => FULL_SUBAGENT_TOOL_DESCRIPTION,
		full: () => FULL_SUBAGENT_TOOL_DESCRIPTION,
		compactDescription: () => COMPACT_SUBAGENT_TOOL_DESCRIPTION,
		compact: () => COMPACT_SUBAGENT_TOOL_DESCRIPTION,
		safetyGuidance: () => SUBAGENT_SAFETY_GUIDANCE,
		safety: () => SUBAGENT_SAFETY_GUIDANCE,
		agentDir: () => agentDir,
		projectConfigDir: () => projectConfigDir,
	};
	return template.replace(/\{\{(\w+)\}\}/g, (raw, name: string) => {
		const replacement = variables[name];
		if (replacement) return replacement();
		warn(options, `${CUSTOM_TOOL_DESCRIPTION_FILE}: unknown placeholder ${raw} left unchanged.`);
		return raw;
	});
}

function loadCustomToolDescription(options?: ToolDescriptionOptions): string | undefined {
	for (const filePath of customDescriptionPaths(options)) {
		let stat: fs.Stats;
		try {
			stat = fs.statSync(filePath);
		} catch (error) {
			if (
				typeof error === "object" &&
				error !== null &&
				"code" in error &&
				(error as NodeJS.ErrnoException).code === "ENOENT"
			)
				continue;
			warn(
				options,
				`Failed to inspect custom tool description '${filePath}': ${error instanceof Error ? error.message : String(error)}`,
			);
			continue;
		}
		if (!stat.isFile()) {
			warn(options, `Ignoring custom tool description '${filePath}' because it is not a file.`);
			continue;
		}
		if (stat.size > CUSTOM_TOOL_DESCRIPTION_MAX_BYTES) {
			warn(
				options,
				`Ignoring custom tool description '${filePath}' because it is larger than ${CUSTOM_TOOL_DESCRIPTION_MAX_BYTES} bytes.`,
			);
			continue;
		}
		try {
			const template = fs.readFileSync(filePath, "utf-8").trim();
			if (!template) {
				warn(options, `Ignoring empty custom tool description '${filePath}'.`);
				continue;
			}
			const rendered = renderCustomTemplate(template, options).trim();
			if (!rendered) {
				warn(options, `Ignoring custom tool description '${filePath}' because it rendered empty.`);
				continue;
			}
			return rendered;
		} catch (error) {
			warn(
				options,
				`Failed to read custom tool description '${filePath}': ${error instanceof Error ? error.message : String(error)}`,
			);
		}
	}
	return undefined;
}

function withMandatorySafetyGuidance(description: string): string {
	const customDescription = description
		.split(SUBAGENT_SAFETY_GUIDANCE)
		.map((part) => part.trim())
		.filter(Boolean)
		.join("\n\n");
	return customDescription ? `${customDescription}\n\n${SUBAGENT_SAFETY_GUIDANCE}` : SUBAGENT_SAFETY_GUIDANCE;
}

export function buildSubagentToolDescription(
	config: Pick<ExtensionConfig, "toolDescriptionMode"> = {},
	options?: ToolDescriptionOptions,
): string {
	const mode = resolveToolDescriptionMode(config, options);
	if (mode === "compact") return COMPACT_SUBAGENT_TOOL_DESCRIPTION;
	if (mode === "custom") {
		const custom = loadCustomToolDescription(options);
		if (custom) return withMandatorySafetyGuidance(custom);
		warn(
			options,
			`${CUSTOM_TOOL_DESCRIPTION_FILE} was not found or valid for toolDescriptionMode "custom"; using full description.`,
		);
	}
	return FULL_SUBAGENT_TOOL_DESCRIPTION;
}
